import Layout from "../../components/Layout";
import { Helmet } from "react-helmet-async";
import {
  FiUsers,
  FiFileText,
  FiClock,
  FiBriefcase,
  FiTrendingUp,
  FiCalendar,
  FiCheckCircle,
  FiBarChart2,
  FiSettings,
} from "react-icons/fi";

const ModuleHumanResources = () => {
  const trackCTA = () => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "click", {
        event_category: "CTA",
        event_label: "Sign Up - HR Page",
      });
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Human Resources | RedERP</title>
        <meta
          name="description"
          content="Manage employee records, payroll, attendance, contracts, leaves, and performance — all in one place with RedERP's HR module."
        />
      </Helmet>

      {/* Hero Block */}
      <section className="bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-10 relative">
          {/* Text */}
          <div className="flex-1 text-left mt-16">
            <h1 className="text-4xl font-bold mb-4">Human Resources</h1>
            <p className="mb-6 text-sm sm:text-base">
              Centralize HR operations. Track employee data, payrolls, leaves, performance, and more — securely and efficiently.
            </p>
            <a
              href="https://app.rederp.pro/sign-up"
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackCTA}
              className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
            >
              Get Started Now
            </a>
          </div>

          {/* Image */}
          <div className="flex-1 hidden md:flex justify-end mt-12 mb-0">
            <img
              src="/assets/images/hr1.png"
              alt="HR Illustration"
              className="w-[420px] lg:w-[420px] xl:w-[420px] h-[310px] object-contain"
            />
          </div>
        </div>
      </section>

      {/* HR Features */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 text-left">
          {[
            {
              icon: <FiUsers size={24} />,
              title: "Employee Database",
              desc: "Store detailed employee records, contracts, documents, and history in one secure place.",
            },
            {
              icon: <FiClock size={24} />,
              title: "Attendance & Time Tracking",
              desc: "Track clock-ins, working hours, and absences with automated attendance records.",
            },
            {
              icon: <FiCalendar size={24} />,
              title: "Leave Management",
              desc: "Enable employees to request leaves and managers to approve with real-time balances.",
            },
            {
              icon: <FiBriefcase size={24} />,
              title: "Payroll & Payslips",
              desc: "Automate salary calculations, deductions, benefits, and generate payslips instantly.",
            },
            {
              icon: <FiTrendingUp size={24} />,
              title: "Performance Reviews",
              desc: "Schedule appraisals, track KPIs, set goals, and record feedback for each employee.",
            },
            {
              icon: <FiFileText size={24} />,
              title: "Contract Management",
              desc: "Create, renew, or terminate contracts while tracking probation and contract terms.",
            },
            {
              icon: <FiBarChart2 size={24} />,
              title: "HR Reports",
              desc: "Get workforce analytics, headcount trends, and leave summaries across teams.",
            },
            {
              icon: <FiCheckCircle size={24} />,
              title: "Compliance & Audit Logs",
              desc: "Ensure all actions and changes are logged with full HR compliance trail.",
            },
            {
              icon: <FiSettings size={24} />,
              title: "Fully Configurable",
              desc: "Adapt HR workflows, job roles, leave types, and pay structures to your organization.",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="p-6 bg-gray-50 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-3 text-primary mb-3">
                {item.icon}
                <h3 className="font-bold">{item.title}</h3>
              </div>
              <p className="text-sm text-gray-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white text-center">
        <div className="max-w-xl mx-auto px-6">
          <h3 className="text-3xl font-bold mb-4">Empower Your HR Team</h3>
          <p className="mb-6">Start your 7-day free trial and bring efficiency to your people operations.</p>
          <a
            href="https://app.rederp.pro/sign-up"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
          >
            Try HR Module Now
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default ModuleHumanResources;
